// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * Hook for fetching and managing accounting periods.
 * Uses React Query for data fetching and caching.
 */
import { getAccountingPeriods } from '@/accounting/api/get-accounting-periods';
import { SavedAccountingPeriodActivityStatus } from '@/accounting/api/types';
import { useQuery } from '@tanstack/react-query';

/**
 * Result type for the useAccountingPeriods hook.
 */
interface UseAccountingPeriodsResult {
  /**
   * All periods from the backend API.
   */
  backendPeriods: SavedAccountingPeriodActivityStatus[];
  /**
   * Whether the query is currently loading.
   */
  isLoading: boolean;
  /**
   * Whether any request is in flight, including a background refetch of cached periods.
   */
  isFetching: boolean;
  /**
   * Whether the query encountered an error.
   */
  isError: boolean;
  /**
   * The error object if the query failed.
   */
  error: unknown;
}

/**
 * React hook that fetches accounting periods from the API.
 *
 * @returns Object containing backend periods and query state
 */
export function useAccountingPeriods(): UseAccountingPeriodsResult {
  const { data, isLoading, isFetching, isError, error } = useQuery({
    queryKey: ['accounting', 'periods'],
    queryFn: async () => {
      const response = await getAccountingPeriods();
      return response.periods;
    },
  });

  return {
    backendPeriods: data ?? [],
    isLoading,
    isFetching,
    isError,
    error,
  };
}
